import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { newsAPI } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorBoundary from '../components/ErrorBoundary';
import SiteAdmin from '../components/SiteAdmin';
import HeroImageAdmin from '../components/HeroImageAdmin';
import EnquiriesAdmin from '../components/EnquiriesAdmin';
import './AdminPage.css';

function AdminPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('content');
  const [contentList, setContentList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Form state (news article)
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [publishNow, setPublishNow] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadContent = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const items = await newsAPI.listAdmin();
      setContentList(Array.isArray(items) ? items : []);
    } catch (err) {
      setError(err.message);
      console.error('Error loading news:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab !== 'content') return;
    let ignore = false;
    ;(async () => {
      try {
        await loadContent();
      } catch (err) {
        if (!ignore) console.error('Error loading news:', err);
      }
    })();
    return () => { ignore = true; };
  }, [activeTab, loadContent]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim()) { setError('Title is required and cannot be empty'); return; }
    if (!content.trim()) { setError('Content is required and cannot be empty'); return; }
    if (title.trim().length > 200) { setError('Title must be less than 200 characters'); return; }

    try {
      setIsSubmitting(true);
      setError(null);
      setSuccess(null);

      const response = await newsAPI.create({
        title: title.trim(),
        content: content.trim(),
        status: publishNow ? 'PUBLISHED' : 'DRAFT',
      });

      if (response.success) {
        setSuccess(`News article ${publishNow ? 'published' : 'saved as draft'} successfully!`);
        setTitle('');
        setContent('');
        setPublishNow(false);
        await loadContent();
        setTimeout(() => setSuccess(null), 5000);
      } else {
        throw new Error(response.message || 'Failed to save news article');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error saving news:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelEdit = () => {
    setTitle('');
    setContent('');
    setPublishNow(false);
    setError(null);
    setSuccess(null);
  };

  // Publish/unpublish an existing article from the list.
  const toggleStatus = async (item) => {
    const next = item.status === 'PUBLISHED' ? 'DRAFT' : 'PUBLISHED';
    try {
      setError(null);
      await newsAPI.setStatus(item.news_id, next);
      await loadContent();
    } catch (err) {
      setError(err.message);
    }
  };

  const removeArticle = async (item) => {
    if (!window.confirm(`Delete "${item.title || 'Untitled'}"? This cannot be undone.`)) return;
    try {
      setError(null);
      await newsAPI.remove(item.news_id);
      await loadContent();
    } catch (err) {
      setError(err.message);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  };

  return (
    <ErrorBoundary>
      <div className="admin-page">
        <header className="admin-header">
          <h1>Admin Dashboard</h1>
          <p>Manage content and settings for Wingbeat Paragliding Club</p>
        </header>

        <div className="admin-tabs">
          <button 
            className={`tab-button ${activeTab === 'content' ? 'active' : ''}`}
            onClick={() => setActiveTab('content')}
          >
            Quick Content
          </button>
          <button 
            className={`tab-button ${activeTab === 'hero' ? 'active' : ''}`}
            onClick={() => setActiveTab('hero')}
          >
            Hero Images
          </button>
          <button 
            className={`tab-button ${activeTab === 'sites' ? 'active' : ''}`}
            onClick={() => setActiveTab('sites')}
          >
            Sites Management
          </button>
          <button 
            className={`tab-button ${activeTab === 'enquiries' ? 'active' : ''}`}
            onClick={() => setActiveTab('enquiries')}
          >
            Enquiries
          </button>
        </div>

        <div className="admin-content">
          {activeTab === 'content' && (
            <>
              {/* Enhanced Content Management Link */}
              <div className="enhanced-content-notice">
                <div className="notice-content">
                  <h3>🚀 Enhanced Content Management Available</h3>
                  <p>Use our new professional content editor with advanced features including:</p>
                  <ul>
                    <li>Rich text editing with preview</li>
                    <li>Content scheduling and lifecycle management</li>
                    <li>SEO optimization tools</li>
                    <li>Bulk operations and analytics</li>
                    <li>Categories and tagging system</li>
                  </ul>
                  <button 
                    onClick={() => navigate('/admin/content')}
                    className="btn-enhanced"
                  >
                    Open Enhanced Content Manager
                  </button>
                </div>
              </div>

              {error && (
                <div className="alert alert-error">
                  <span className="alert-icon">⚠️</span>
                  <span className="alert-message">{error}</span>
                  <button className="alert-close" onClick={() => setError(null)}>×</button>
                </div>
              )}

              {success && (
                <div className="alert alert-success">
                  <span className="alert-icon">✅</span>
                  <span className="alert-message">{success}</span>
                  <button className="alert-close" onClick={() => setSuccess(null)}>×</button>
                </div>
              )}
        <section className="content-form">
          <div className="form-header">
            <h2>Create News Article</h2>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="title">Title</label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter article title (max 200 characters)"
                disabled={isSubmitting}
                maxLength="200"
                required
              />
              <small className="form-help">{title.length}/200 characters</small>
            </div>

            <div className="form-group">
              <label htmlFor="content">Content</label>
              <textarea
                id="content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Enter the article body. Use line breaks for paragraphs."
                rows="12"
                disabled={isSubmitting}
                required
              />
              <small className="form-help">
                Tip: Use line breaks to separate paragraphs. Content will be formatted automatically.
              </small>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={publishNow}
                  onChange={(e) => setPublishNow(e.target.checked)}
                  disabled={isSubmitting}
                />
                {' '}Publish immediately (otherwise saved as a draft)
              </label>
            </div>

            <div className="form-actions">
              <button type="submit" disabled={isSubmitting} className="btn-primary">
                {isSubmitting ? (
                  <>
                    <span className="spinner"></span>
                    {publishNow ? 'Publishing...' : 'Saving draft...'}
                  </>
                ) : (
                  publishNow ? 'Publish Article' : 'Save Draft'
                )}
              </button>

              {(title || content) && !isSubmitting && (
                <button type="button" onClick={cancelEdit} className="btn-secondary">
                  Clear Form
                </button>
              )}
            </div>
          </form>
        </section>

        <section className="content-list">
          <div className="section-header">
            <h2>News Articles</h2>
            <button onClick={loadContent} disabled={loading} className="btn-secondary">
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
          
          {loading ? (
            <LoadingSpinner message="Loading news..." />
          ) : error && contentList.length === 0 ? (
            <div className="error-state">
              <p>Failed to load news. Please try refreshing.</p>
              <button onClick={loadContent} className="btn-primary">
                Retry
              </button>
            </div>
          ) : contentList.length === 0 ? (
            <div className="empty-state">
              <h3>No news articles yet</h3>
              <p>Create your first article using the form above.</p>
            </div>
          ) : (
            <div className="content-items">
              {contentList.map((item, index) => (
                <div
                  key={item.news_id || item.PK || index}
                  className="content-item"
                >
                  <div className="content-item-header">
                    <h3>{item.title || 'Untitled'}</h3>
                    <div className="content-badges">
                      {item.status && (
                        <span className={`status-badge ${item.status.toLowerCase()}`}>
                          {item.status}
                        </span>
                      )}
                    </div>
                  </div>

                  <p className="content-preview">
                    {item.content ? (
                      <>
                        {item.content.replace(/<[^>]*>/g, '').substring(0, 200)}
                        {item.content.length > 200 ? '...' : ''}
                      </>
                    ) : (
                      <em>No content</em>
                    )}
                  </p>

                  <div className="content-item-footer">
                    <div className="content-meta">
                      <span className="content-date">
                        Updated: {formatDate(item.updated_date)}
                      </span>
                      {item.updated_by && (
                        <span className="content-author">
                          by {item.updated_by}
                        </span>
                      )}
                    </div>

                    <div className="content-actions">
                      <button
                        onClick={() => navigate(`/admin/content/edit/${encodeURIComponent(item.news_id)}`)}
                        className="btn-secondary"
                        disabled={isSubmitting}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => toggleStatus(item)}
                        className="btn-secondary"
                        disabled={isSubmitting}
                      >
                        {item.status === 'PUBLISHED' ? 'Unpublish' : 'Publish'}
                      </button>
                      <button
                        onClick={() => removeArticle(item)}
                        className="btn-secondary btn-danger"
                        disabled={isSubmitting}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
            </>
          )}

          {activeTab === 'hero' && (
            <HeroImageAdmin />
          )}

          {activeTab === 'sites' && (
            <SiteAdmin />
          )}

          {activeTab === 'enquiries' && (
            <EnquiriesAdmin />
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
}

export default AdminPage;
